import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Target, Users, Award, TrendingUp } from 'lucide-react';
import { useConference } from '@/context/ConferenceContext';

const About = () => {
  const { importantDetails, siteConfig } = useConference();
  const stats = [
    { icon: Users, label: 'Expected Attendees', value: '500+' },
    { icon: Award, label: 'Keynote Speakers', value: '8' },
    { icon: TrendingUp, label: 'Technical Sessions', value: '50+' },
    { icon: Target, label: 'Countries', value: '30+' },
  ];

  return (
    <>
      <Helmet>
        <title>About - {importantDetails?.ConferenceTitle || siteConfig?.name || 'Conference'}</title>
        <meta name="description" content="Learn about the International Conference on Civil Engineering 2026" />
      </Helmet>

      <div className="bg-white">
        {/* Hero */}
        <section className="hero-gradient py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center"
            >
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">About CCAI-2026</h1>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Building bridges between research, practice, and innovation in civil engineering
              </p>
            </motion.div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-12 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {stats.map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="text-center p-6 rounded-2xl bg-gray-50"
                    data-testid={`stat-${index}`}
                  >
                    <div className="inline-flex w-12 h-12 rounded-xl bg-sky-100 items-center justify-center mb-3">
                      <Icon className="text-sky-500" size={24} />
                    </div>
                    <div className="text-3xl font-bold text-gray-900 mb-1">{stat.value}</div>
                    <div className="text-sm text-gray-600">{stat.label}</div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Mission */}
        <section className="section-spacing">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">Our Mission</h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              The International Conference on Civil Engineering (ICCE) 2026 aims to provide a premier interdisciplinary platform for researchers, practitioners, and educators to present and discuss the most recent innovations, trends, and concerns in the field of civil engineering.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Our mission is to foster collaboration between academia and industry, facilitate knowledge exchange, and promote sustainable solutions to modern infrastructure challenges. We believe that by bringing together diverse perspectives from structural, geotechnical, transportation, water resources, construction management, and environmental engineering, we can drive meaningful progress in our field.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              CCAI-2026 provides an exceptional opportunity for networking, learning about cutting-edge research, and exploring new technologies that are shaping the future of civil engineering.
            </p>
          </div>
        </section>

        {/* Conference Themes */}
        <section className="section-spacing bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Conference Themes</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="bg-white p-6 rounded-2xl shadow-sm"
              >
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Sustainable Infrastructure</h3>
                <p className="text-gray-600">
                  Exploring eco-friendly materials, green building technologies, and carbon-neutral construction practices that minimize environmental impact while maintaining structural integrity.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="bg-white p-6 rounded-2xl shadow-sm"
              >
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Smart Cities & Digital Transformation</h3>
                <p className="text-gray-600">
                  Leveraging AI, IoT, and BIM technologies to create intelligent infrastructure systems that improve urban living, optimize resource usage, and enhance resilience.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="bg-white p-6 rounded-2xl shadow-sm"
              >
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Climate Resilience</h3>
                <p className="text-gray-600">
                  Designing infrastructure systems that can withstand extreme weather events, rising sea levels, and changing environmental conditions through innovative engineering solutions.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="bg-white p-6 rounded-2xl shadow-sm"
              >
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Advanced Materials & Construction</h3>
                <p className="text-gray-600">
                  Investigating next-generation materials, prefabrication techniques, and construction automation that improve efficiency, reduce costs, and enhance structural performance.
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Organizing Institutions */}
        <section className="section-spacing">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">Organizing Institutions</h2>
            <div className="bg-gray-50 p-8 rounded-2xl">
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                CCAI-2026 is organized by a consortium of leading academic institutions and professional societies:
              </p>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-3">
                  <span className="text-sky-500 font-bold">•</span>
                  <span>Stanford University - Department of Civil and Environmental Engineering</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-sky-500 font-bold">•</span>
                  <span>Massachusetts Institute of Technology (MIT) - School of Engineering</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-sky-500 font-bold">•</span>
                  <span>UC Berkeley - College of Engineering</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-sky-500 font-bold">•</span>
                  <span>American Society of Civil Engineers (ASCE)</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-sky-500 font-bold">•</span>
                  <span>International Association for Bridge and Structural Engineering (IABSE)</span>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* Previous Editions */}
        <section className="section-spacing bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">Previous Editions</h2>
            <div className="space-y-6">
              <div className="bg-white p-6 rounded-2xl shadow-sm">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="text-xl font-semibold text-gray-900">ICCE 2024 - Tokyo, Japan</h3>
                  <span className="text-sm text-gray-500">March 2024</span>
                </div>
                <p className="text-gray-600">
                  Hosted 450+ attendees from 28 countries, featuring 120+ presentations and workshops on earthquake-resistant design and urban infrastructure.
                </p>
              </div>

              <div className="bg-white p-6 rounded-2xl shadow-sm">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="text-xl font-semibold text-gray-900">ICCE 2022 - Berlin, Germany</h3>
                  <span className="text-sm text-gray-500">October 2022</span>
                </div>
                <p className="text-gray-600">
                  Focused on sustainable infrastructure with 380+ participants, showcasing innovative green building technologies and renewable energy integration.
                </p>
              </div>

              <div className="bg-white p-6 rounded-2xl shadow-sm">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="text-xl font-semibold text-gray-900">ICCE 2020 - Singapore</h3>
                  <span className="text-sm text-gray-500">March 2020</span>
                </div>
                <p className="text-gray-600">
                  Explored smart city solutions with 320+ attendees, highlighting IoT applications and data-driven infrastructure management.
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default About;
